import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import { get, intersectionBy, isEmpty, map } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormLazyTreeSelect,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { treeOptionPropType } from '../../../common/prop-types'

import { useUpdateDishTypeAdditionalIngredientsDetailsMutation } from '../../hooks'
import { dishTypePropType } from '../../prop-types'
import { setDishTypePageIsDirty } from '../../redux/actions'

import { dishTypeAdditionalIngredientsFormInitialValues } from './initial-values'

const DishTypeAdditionalIngredientsDetailsForm = ({
  dishType,
  ingredientOptions,
  groupedFlatIngredientTreeDataByValue,
  flatIngredientTreeData,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DISH_TYPE.SAVE_ADDITIONAL_INGREDIENTS,
  ])

  const [
    updateDishTypeAdditionalIngredientsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDishTypeAdditionalIngredientsDetailsMutation()

  const initialValues = useMemo(
    () => dishTypeAdditionalIngredientsFormInitialValues(dishType),
    [dishType],
  )

  const definiteIngredientIdsItemRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          try {
            const otherComponentIngredientIds = getFieldValue(
              'probableIngredientIds',
            )

            const conflicts = intersectionBy(
              otherComponentIngredientIds,
              value,
              'value',
            )

            if (!isEmpty(conflicts)) {
              throw new Error(
                `Items is already present in probableIngredientIds ${map(
                  conflicts,
                  ({ value }) =>
                    get(
                      groupedFlatIngredientTreeDataByValue,
                      `${value}[0].title`,
                    ),
                )}`,
              )
            }

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [groupedFlatIngredientTreeDataByValue],
  )

  const probableIngredientIdsItemRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          try {
            const otherComponentIngredientIds = getFieldValue(
              'definiteIngredientIds',
            )

            const conflicts = intersectionBy(
              otherComponentIngredientIds,
              value,
              'value',
            )

            if (!isEmpty(conflicts)) {
              throw new Error(
                `Items is already present in definiteIngredientIds ${map(
                  conflicts,
                  ({ value }) =>
                    get(
                      groupedFlatIngredientTreeDataByValue,
                      `${value}[0].title`,
                    ),
                )}`,
              )
            }

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [groupedFlatIngredientTreeDataByValue],
  )

  const handleSaveData = useCallback(
    data => updateDishTypeAdditionalIngredientsDetails(dishType.id, data),
    [dishType.id, updateDishTypeAdditionalIngredientsDetails],
  )

  useEffect(() => {
    resetForm()
  }, [dishType.additionalIngredients, resetForm])

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Dish Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    dispatch(setDishTypePageIsDirty({ isAdditionalIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="additionalIngredientsForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row gutter={[16]}>
        <Col span={12}>
          <FormLazyTreeSelect
            disabled={!isFormEnabled}
            form={form}
            name="definiteIngredientIds"
            label="Definite Ingredients"
            showSearch
            labelAbove
            allowClear
            treeCheckable
            treeCheckStrictly
            treeData={ingredientOptions}
            rules={definiteIngredientIdsItemRules}
            placeholder="Select a new definite ingredient"
            groupedFlatTreeDataByValue={groupedFlatIngredientTreeDataByValue}
            flatTreeData={flatIngredientTreeData}
            initialValue={initialValues.definiteIngredientIds}
          />
          <FormLazyTreeSelect
            disabled={!isFormEnabled}
            form={form}
            name="probableIngredientIds"
            label="Probable Ingredients"
            showSearch
            labelAbove
            allowClear
            treeCheckable
            treeCheckStrictly
            treeData={ingredientOptions}
            rules={probableIngredientIdsItemRules}
            placeholder="Select a new probable ingredient"
            groupedFlatTreeDataByValue={groupedFlatIngredientTreeDataByValue}
            flatTreeData={flatIngredientTreeData}
            initialValue={initialValues.probableIngredientIds}
          />
        </Col>
        <Col span={12} />
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DISH_TYPE.SAVE_ADDITIONAL_INGREDIENTS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DishTypeAdditionalIngredientsDetailsForm.propTypes = {
  dishType: dishTypePropType.isRequired,
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType).isRequired,
  groupedFlatIngredientTreeDataByValue: PropTypes.object.isRequired,
  flatIngredientTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default DishTypeAdditionalIngredientsDetailsForm
